import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip, IconButton, Dialog, DialogTitle, DialogContent, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import SpinnerLoader from '../../../Components/SpinnerLoader';
import images from '../../../Utils/images';
import { eventsend } from '../../../Config/analyticsFunctions';
import { workflowAutomationQuota, workflowAutomationPaidHistory, workflowAutomationPaidHistoryReport } from '../../../Services';
import { styledSx } from '../../ContactModule/Tags/variableDependencies';

interface WorkflowHistoryItem {
  _id: string;
  amountDeducted: number;
  prevBalance: number;
  newBalance: number;
  workflowName: string;
  nodeType: string;
  createdAt?: string;
}

interface PaginationData {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface WorkflowHistoryResponse {
  status: number;
  flag: boolean;
  data: {
    data: WorkflowHistoryItem[];
    pagination: PaginationData;
  };
  message: string;
}

interface AutomationTaskModalProps {
  setIsAutomationTaskModalOpen: (open: boolean) => void;
  isAutomationTaskModalOpen: boolean;
  setIsHistoryModalOpen: (open: boolean) => void;
}

const AutomationTaskModal: React.FC<AutomationTaskModalProps> = ({
  setIsAutomationTaskModalOpen,
  isAutomationTaskModalOpen,
  setIsHistoryModalOpen
}) => {
  const authtoken = useSelector((state: any) => state.main.auth_token);
  const localdata = useSelector((s: any) => s.main.localdata);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [quotaData, setQuotaData] = useState({
    remainingQuota: 0,
    executedTasksQuota: 0,
    executedTasksPaid: 0,
    executedTasksFree: 0
  });
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [workflowHistory, setWorkflowHistory] = useState<any[]>([]);

  const handleClose = () => {
    setIsAutomationTaskModalOpen(false);
  };

  useEffect(() => {
    if (isAutomationTaskModalOpen && authtoken) {
      getWorkflowAutomationQuota();
    }
  }, [isAutomationTaskModalOpen, authtoken]);

  useEffect(() => {
    getWorkflowHistoryApiCall();
  }, [page, rowsPerPage]);

  const getWorkflowAutomationQuota = async () => {
    try {
      const res = await workflowAutomationQuota(authtoken);
      const data = res?.data || {};
      setQuotaData({
        remainingQuota: data.remainingQuota || 0,
        executedTasksQuota: data.executedTasksQuota || 0,
        executedTasksPaid: data.executedTasksPaid || 0,
        executedTasksFree: data.executedTasksFree || 0
      });
      eventsend('workflow_task_breakdown_viewed', {
        timestamp: new Date().toISOString(),
        account_id: localdata?._id,
        tasks_count:(data.executedTasksPaid || 0) + (data.executedTasksFree || 0),
        tasks_paid:(data.executedTasksPaid || 0),
        tasks_free:(data.executedTasksFree || 0)
      });
    } catch (err) {
      console.error('Error fetching workflow automation quota:', err);
    }
  };

  const getWorkflowHistoryApiCall = async () => {
    setLoading(true);
    try {
      const params = { limit: rowsPerPage, page };
      const res: WorkflowHistoryResponse = await workflowAutomationPaidHistory(params, authtoken);
      const apiRows = res?.data?.data || [];

      const rows = apiRows.map((item, index) => ({
        id: item._id || index,
        taskname: item.nodeType || 'N/A',
        name: item.workflowName || 'N/A',
        amount: '₹0.25',
        debiton: item.createdAt ? new Date(item.createdAt).toLocaleString() : 'N/A',
        remainingbalance: `₹${Number(item.newBalance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }));

      setWorkflowHistory(rows);
      setTotalRows(res?.data?.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching workflow history:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCreditReport = async () => {
    try {
      const csvBlob = await workflowAutomationPaidHistoryReport(authtoken);

      if (!csvBlob || !(csvBlob instanceof Blob)) {
        console.error('Invalid Blob received from API.');
        return;
      }

      const blob = new Blob([csvBlob], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Automation_Task_Report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV file:', error);
    }
  };

  const columns: GridColDef[] = [
    { field: 'taskname', headerName: 'Task Name', flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'name', headerName: 'Workflow Name', flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'amount', headerName: 'Amount', flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'debiton', headerName: 'Debit on', flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'remainingbalance', headerName: 'Remaining Balance', flex: 1, align: 'center', headerAlign: 'center' }
  ];

  return (
    <Dialog
      open={isAutomationTaskModalOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: { xs: 2, md: 4 },
          m: { xs: 1, md: 4 },
          width: '100%',
          maxWidth: '1200px'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pb: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src={images.BackArrowBlack}
            style={{ height: 24, width: 24, objectFit: 'contain', cursor: 'pointer' }}
            onClick={() => {
              setIsAutomationTaskModalOpen(false);
              setIsHistoryModalOpen(true);
            }}
          />
          <span style={{ fontWeight: 600, fontSize: 18 }}>Automation Task</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Tooltip title={'Download Report'}>
            <img
              src={images.DOWNLOAD_BLACK}
              alt="download credit report"
              onClick={getCreditReport}
              style={{ height: 22, width: 22, objectFit: 'contain', cursor: 'pointer' }}
            />
          </Tooltip>
          <IconButton onClick={handleClose} size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Stats Cards */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 24
          }}
        >
          {[
            { label: 'Total Task', value: quotaData.executedTasksQuota },
            { label: 'Quota Task', value: quotaData.executedTasksFree },
            { label: 'Paid Task', value: quotaData.executedTasksPaid },
            { label: 'Amount Used', value: `₹${(quotaData.executedTasksPaid * 0.25).toLocaleString()}` }
          ].map((stat, i) => (
            <div
              key={i}
              style={{
                flex: '1 1 22%',
                minWidth: 150,
                padding: '16px 12px',
                borderRadius: 8,
                border: '1px solid #E6E6E6',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: 16
              }}
            >
              {`${stat.label}: ${stat.value}`}
            </div>
          ))}
        </div>


        {loading ? (
          <div className="d-flex flex-column justify-content-center align-items-center w-100" style={{ flex: 1 }}>
            <SpinnerLoader />
          </div>
        ) : (
          <>


            <div style={{ overflowX: 'auto', width: '100%' }}>
              <div style={{ minWidth: 700 }}>
                <DataGrid
                  rows={workflowHistory}
                  columns={columns}
                  paginationMode="server"
                  rowCount={totalRows}
                  pageSizeOptions={[10, 25, 50]}
                  pagination
                  paginationModel={{ page, pageSize: rowsPerPage }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setRowsPerPage(model.pageSize);
                  }}
                  disableRowSelectionOnClick
                    sx={{
                    ...styledSx,
                    height: 400,
                    '& .MuiDataGrid-virtualScroller': {
                      overflow: 'auto'
                    }
                  }}
                />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AutomationTaskModal;